import React from 'react';
import { Box, Typography } from '@mui/material';
import { LockOutlined as LockIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';

/**
 * On-theme ("Direction One" / Document) indicator for features that require a
 * paid plan. Replaces the legacy blue-gradient ProBadge inside trip surfaces so
 * premium cues match the oxblood/brass document styling.
 *
 * variants:
 *  - 'pill' (default): brass-outlined pill with a lock icon + "PRO" label
 *  - 'icon': compact brass lock chip (for tight spaces like tab labels)
 */
export function PaidFeatureLabel({ show = true, variant = 'pill', children, sx = {} }) {
  if (!show) return children;
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, ...sx }}
    >
      <Box component="span">{children}</Box>
      <DocumentProBadge variant={variant} />
    </Box>
  );
}

const DocumentProBadge = ({ variant = 'pill', label, showIcon = true, sx = {} }) => {
  const { t } = useTranslation();
  const text = label || t('payment.proBadge', 'PRO');

  if (variant === 'icon') {
    return (
      <Box
        component="span"
        data-testid="paid-feature-badge"
        aria-label={t('payment.premiumFeature', 'Premium feature')}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: '50%',
          bgcolor: DOCUMENT.accent,
          color: DOCUMENT.ivory,
          ...sx,
        }}
      >
        <LockIcon sx={{ fontSize: 10 }} />
      </Box>
    );
  }

  return (
    <Box
      component="span"
      data-testid="paid-feature-badge"
      aria-label={t('payment.premiumFeature', 'Premium feature')}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.375,
        px: 0.75,
        py: 0.125,
        borderRadius: '2px',
        border: `1px solid ${DOCUMENT.accent}`,
        color: DOCUMENT.accent,
        lineHeight: 1,
        ...sx,
      }}
    >
      {showIcon && <LockIcon sx={{ fontSize: 11 }} />}
      <Typography
        component="span"
        sx={{
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          lineHeight: 1,
          color: 'inherit',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

export default DocumentProBadge;
